/**
 * Life4Earth V2 — hook for the current user's follows (onboarding chunk A).
 *
 * Feeds the Getting-started "Follow 3 projects or communities" checklist
 * row (§7.7 r1 item 3): completion computes LIVE from the follow count —
 * the same computed-not-stored pattern as the rung basics (which count
 * entity_follows server-side in OnboardingRung.RungBasicsCompleteAsync).
 *
 * Pattern follows useApiOnboarding.ts (queryCache + focus refetch, so a
 * user who follows 3 things in Discover and returns to the Action Center
 * sees the row complete without a hard reload).
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { getMyFollows } from "./follows";
import type { FollowItem } from "./follows";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

const CACHE_KEY = "myFollows";

export interface UseApiMyFollowsResult {
  follows: FollowItem[];
  followCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApiMyFollows(enabled: boolean = true): UseApiMyFollowsResult {
  const mountedRef = useRef(true);
  const lastFetchAtRef = useRef(0);
  const [follows, setFollows] = useState<FollowItem[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!mountedRef.current || !enabled) return;
    lastFetchAtRef.current = Date.now();
    try {
      const res = await fetchForKey(CACHE_KEY, async () => getMyFollows(), {
        force: true,
      });
      if (!mountedRef.current) return;
      setFollows(res || []);
      setLoading(false);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setLoading(false);
      setError(err instanceof Error ? err.message : "Failed to load follows");
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      refetch();
    } else {
      setFollows([]);
      setLoading(false);
    }
    return () => {
      mountedRef.current = false;
    };
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === CACHE_KEY) refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, handler);
    return () => window.removeEventListener(CACHE_UPDATE_EVENT, handler);
  }, [enabled, refetch]);

  // Focus refetch (throttled) — mirrors useApiOnboarding. The checklist
  // completes from live data; returning from Discover should reflect the
  // new follows promptly.
  useEffect(() => {
    if (!enabled) return;
    const THROTTLE_MS = 15_000;
    const maybeRefetch = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastFetchAtRef.current < THROTTLE_MS) return;
      refetch();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") maybeRefetch();
    };
    window.addEventListener("focus", maybeRefetch);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", maybeRefetch);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, refetch]);

  return { follows, followCount: follows.length, loading, error, refetch };
}
